/**
 * TeamDetail — a team as a canvas with panes floating over its edges.
 *
 * WHAT CHANGED, AND WHY.
 *
 * The page was a `FilterBarWithTabs` over a fixed `lg:grid-cols-3`, whose
 * right column held a hand-rolled Members card — its own avatar list, its own
 * "+8 more" tail, and a Manage button carrying a thirty-line side-panel
 * payload written inline in JSX. That column only existed on the Details tab,
 * so "who is on this team" left the screen the moment anyone opened the Feed.
 * The audit log was reachable two ways that disagreed: a `RecentActivityCard`
 * on the canvas showing a truncated copy, and an `ActivityDrawer` — a
 * `fixed inset-y-0 right-0` panel with its own scrim, outside every layout
 * the page had.
 *
 * All of it is now PANES on the shared `DetailPageSurface`, so they dock left
 * or right, stack, reorder by dragging their header, resize, and are
 * remembered per user per team. See `surface/layout`.
 *
 * THE LAYERING, outside in:
 *
 *   DetailFrame        breadcrumbs, the overflow menu, the header avatars
 *     DetailTabBar     the canvas's tabs + the pane rail, full width
 *     DetailPageSurface  the canvas, with the panes over its edges
 *
 * The tab bar is a SIBLING of the surface, not its first child: navigation is
 * the frame around the work, so a docked pane must not sit over it.
 *
 * THE MEMBERS PANE IS THE SHARED ONE. `AccessPane` with `ownerLabel="Lead"`,
 * fed by `surface/orgMembers` — the same list anatomy a matter, a project and
 * a task draw, and the same rule: the pane READS, its footer button is the
 * one way to the transactional editor.
 */

import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useTranslation } from 'react-i18next';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import apis from '../../../utils/apis';
import {
  PiArrowLeftLight,
  PiPencilLight,
  PiTrashLight,
  PiPlusCircleLight,
  PiTextAlignLeftLight,
  PiCircleDashedLight,
  PiUsersLight,
  PiCalendarBlankLight,
  PiClockCounterClockwiseLight,
  PiTagLight,
  PiInfoLight,
  PiFileTextLight,
} from 'react-icons/pi';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import Spinner from '../../shared/TailwindComponents/Spinner';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import { FactTile, FieldGrid } from '../../shared/brand/Field';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { fetchPracticeAreas } from '../../../store/actions/DropdownActions';
import {
  fetchTeamDetails,
  updateTeam,
  addTeamMember,
  removeTeamMember,
  fetchTeamFeed,
  postTeamFeedEvent,
  deleteTeam,
} from '../../../store/actions/organization/teamActions';
import { ActivityFeed } from '../../shared/feed';
import DetailFrame from '../../shared/Frames/DetailFrame';
import DetailTabBar, { TabItem } from '../../shared/surface/DetailTabBar';
import DetailPageSurface from '../../shared/surface/DetailPageSurface';
import PaneRail from '../../shared/surface/PaneRail';
import PaneBody from '../../shared/surface/PaneBody';
import DocumentsTab from '../Shared/DocumentsTab';
import OrgReaderPane from '../Shared/OrgReaderPane';
import { OrgSurfaceProvider } from '../Shared/OrgSurfaceContext';
import { formatDate } from '../../../utils/functions';
import AuditLogPanel from '../../AuditLog/AuditLogPanel';
import AccessPane from '../../shared/access/AccessPane';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, teamDetailTourSteps } from '../../shared/FeatureTour';
import { TEAM_SURFACE, TEAM_TABS, useTeamWorkspace } from './surface';
import OrgContextPane from './surface/OrgContextPane';
import { orgAccessPanelPayload, toAccessRows } from './surface/orgMembers';

interface Team {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  member_count: number;
  members: Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role?: string;
    firm_role?: string;
    joined_at?: string;
    profile_picture?: string | null;
  }>;
  created_at?: string;
  department?: string;
  practice_area?: string;
  practice_areas?: Array<{ id: number; name: string }>;
  practice_area_ids?: number[];
  document_count?: number;
}

interface TeamDetailParams {
  teamId: string;
}

const TeamDetail: React.FC = () => {
  const { t } = useTranslation();
  const { teamId } = useParams<TeamDetailParams>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const practiceAreas = useSelector((state: RootState) => state.dropdown.dropdowns.practiceAreas) || [];

  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feed, setFeed] = useState<any[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [uploadingDocCount, setUploadingDocCount] = useState(0);

  const [isEditingTeamInfo, setIsEditingTeamInfo] = useState(false);
  const [editedTeamFields, setEditedTeamFields] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);

  /**
   * The workspace: pane layout and the active tab, from the ONE shared hook.
   * Keyed by team id AND by user, so each team remembers its own arrangement
   * and nobody inherits a colleague's — see `surface/layout`.
   */
  const {
    panels,
    api: surfaceApi,
    activeTab,
    goToTab,
    readerContent,
    setReaderContent,
  } = useTeamWorkspace(teamId);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'team-detail',
    steps: teamDetailTourSteps,
    readyToShow: !loading && !!team,
    autoStartDelay: 600,
  });

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  useEffect(() => {
    dispatch(fetchPracticeAreas());
  }, [dispatch]);

  const practiceAreaOptions = practiceAreas.map((area: any) => ({
    value: String(area.value || area.id),
    label: String(area.label || area.name),
  }));

  const loadTeam = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await dispatch(fetchTeamDetails(teamId) as any);
      if (result) {
        setTeam(result);
        setEditedTeamFields(result);
      } else {
        setError(t('Failed to load team'));
      }
    } catch (err) {
      setError(t('Failed to load team'));
    } finally {
      setLoading(false);
    }
  }, [teamId, dispatch, t]);

  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const result = await dispatch(fetchTeamFeed(teamId) as any);
      if (Array.isArray(result)) setFeed(result);
    } finally {
      setFeedLoading(false);
    }
  }, [teamId, dispatch]);

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const response = await apis.get(`/org-people/teams/${teamId}/documents`);
      setDocuments(Array.isArray(response.data.documents) ? response.data.documents : []);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    loadTeam();
    loadFeed();
  }, [teamId, loadTeam, loadFeed]);

  useEffect(() => {
    if (activeTab === 'Documents') loadDocuments();
    if (activeTab === 'Feed') loadFeed();
  }, [activeTab, loadDocuments, loadFeed]);

  const handleTeamFieldChange = (field: string, value: any) => {
    setEditedTeamFields((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSaveTeam = async () => {
    if (!team?.id) return;
    setIsSaving(true);
    try {
      await dispatch(updateTeam(team.id, {
        name: editedTeamFields.name,
        description: editedTeamFields.description,
        practice_area_ids: (editedTeamFields.practice_area_ids || []).map(Number),
      }));
      setIsEditingTeamInfo(false);
      await loadTeam();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Team updated successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (err) {
      console.error('Error saving team:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to save team'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTeamInfo(false);
    setEditedTeamFields(team || {});
  };

  /**
   * The manage editor, lifted out of the JSX it used to live in.
   *
   * It was the `onClick` of a Button nested inside a GlassCard's `actions`
   * prop — thirty lines of payload authored where nobody would look for it,
   * and duplicated verbatim on the department page. It is one call now, into
   * the shared builder.
   */
  const handleManageAccess = useCallback(() => {
    dispatch(showSidePanel({
      ...orgAccessPanelPayload({
        resourceUuid: teamId,
        resourceType: 'team',
        members: team?.members,
        title: t('Manage Members'),
        subMsg: t('Add or remove members of this team.'),
        icon: <PiUsersLight className="w-5 h-5" />,
        currentUserUuid: userInfo?.UserID ? String(userInfo.UserID) : undefined,
        addMember: (uuid, userUuid) => dispatch(addTeamMember(uuid, userUuid, 'member') as any),
        removeMember: (uuid, collabUuid) => dispatch(removeTeamMember(uuid, collabUuid) as any),
        onUpdate: () => { loadTeam(); },
      }),
      onCancel: () => dispatch(hideSidePanel()),
    } as any));
  }, [dispatch, teamId, team, userInfo, loadTeam, t]);

  const handlePostFeedEvent = async (eventData: { event_type: string; message: string; metadata?: any }) => {
    try {
      await dispatch(postTeamFeedEvent(teamId, eventData));
      await loadFeed();
    } catch (err) {
      console.error('Error posting feed event:', err);
    }
  };

  const handleAddComment = async (eventId: string, comment: string) => {
    try {
      if (!team?.id) throw new Error('Team ID not available');
      await apis.post(`/org-people/teams/${team.id}/feed/${eventId}/comments`, { message: comment });
      await loadFeed();
    } catch (err) {
      console.error('Error adding comment:', err);
      const errorMessage = err instanceof Error ? err.message : t('Unknown error');
      dispatch(showFeedbackModal({
        showModal: true,
        message: `${t('Comment failed')}: ${errorMessage}`,
        modalType: 'error',
        duration: 5000,
      }));
    }
  };

  const handleDeleteTeam = () => {
    dispatch(showModal({
      type: 'confirmation',
      title: t('Delete Team'),
      message: t('This team and all associated data will be permanently deleted.'),
      subText: t('This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Delete'),
      showModal: true,
      onConfirm: async () => {
        if (!team?.id) return;
        const result = await dispatch(deleteTeam(team.id) as any);
        dispatch(hideModal());
        if (result) history.push('/teams');
      },
    } as any));
  };

  const handleUploadDocument = async (files: File[]) => {
    setIsUploadingDocs(true);
    setUploadingDocCount(files.length);
    dispatch(hideModal());
    try {
      const formData = new FormData();
      for (const file of files) formData.append('files', file);

      const response = await apis.post(`/org-people/teams/${teamId}/documents/bulk-upload`, formData);

      if (response?.data?.success) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.message || t('Documents uploaded successfully'),
          modalType: 'success',
          duration: 3000,
        }));
      }

      await loadDocuments();
      await loadTeam();
      await loadFeed();
    } catch (err) {
      console.error('Error uploading document:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `${t('Upload failed')}: ${err instanceof Error ? err.message : t('Unknown error')}`,
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsUploadingDocs(false);
      setUploadingDocCount(0);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await apis.delete(`/org-people/teams/${teamId}/documents/${documentId}`);
      await loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `${t('Delete failed')}: ${err instanceof Error ? err.message : t('Unknown error')}`,
        modalType: 'error',
        duration: 3000,
      }));
    }
  };

  const handleBulkDeleteDocuments = async (documentIds: string[]) => {
    await apis.post(`/org-people/teams/${teamId}/documents/bulk-delete`, { document_ids: documentIds });
    await loadDocuments();
    await loadTeam();
  };

  const handleOpenDocumentUploadModal = () => {
    dispatch(showModal({
      type: 'uploadAttachment',
      message: t('Upload Team Document'),
      icon: <PiPlusCircleLight className="w-6 h-6" />,
      showModal: true,
      onConfirm: handleUploadDocument,
    }));
  };

  /* The VALUES come from the surface config — one vocabulary for the bar and
     `goToTab`. Only the labels are the page's. */
  const TAB_LABELS: Record<typeof TEAM_TABS[number], string> = {
    Details: t('Details'),
    Feed: t('Feed'),
    Documents: t('Documents'),
  };
  const tabs: TabItem[] = TEAM_TABS.map((value) => ({ label: TAB_LABELS[value], value }));

  const breadcrumbs = [
    { name: t('Teams'), href: '/teams', current: false },
    { name: team?.name || t('Team'), href: `/teams/${teamId}`, current: true },
  ];

  if (loading) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} showBackButton>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 w-full">
            <DetailTabBar tabs={tabs} activeTab="Details" onTabClick={() => {}} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <Skeleton variant="detail" surface={TEAM_SURFACE} />
          </div>
        </div>
      </DetailFrame>
    );
  }

  if (error || !team) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} showBackButton>
        <div className="text-center py-12">
          <p className="text-danger-600 dark:text-danger-400 mb-4">{error || t('Team not found')}</p>
          <Button onClick={() => history.push('/teams')}>
            <PiArrowLeftLight className="w-4 h-4 mr-2" />
            {t('Back to Teams')}
          </Button>
        </div>
      </DetailFrame>
    );
  }

  const members = team.members || [];

  return (
    /* The provider is what lets the Documents tab — three components down —
       put a file in the reader pane without "open the reader" being threaded
       through as a prop. Outside it the same tab falls back to a side panel. */
    <OrgSurfaceProvider value={surfaceApi}>
    <DetailFrame
      breadcrumbs={breadcrumbs}
      dropdownActions={[
        {
          label: t('Manage Members'),
          icon: <PiUsersLight className="w-4 h-4" />,
          onClick: () => surfaceApi.openPane('access'),
        },
        // The activity log is a PANE now, not a drawer: the menu item raises it.
        {
          label: t('Activity log'),
          icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
          onClick: () => surfaceApi.openPane('activity'),
        },
        {
          label: t('Delete'),
          icon: <PiTrashLight className="w-4 h-4" />,
          onClick: handleDeleteTeam,
        },
      ]}
      subscriptionActive
      showBackButton
      /* The header stack RAISES THE MEMBERS PANE — it used to be decorative,
         four `ProfileImage`s in a hand-rolled `-space-x-1.5` div with a
         `bg-gray-200` "+N" tail that ignored dark mode's tokens. */
      additionalHeaderElements={members.length > 0 ? (
        <CollaboratorStack
          collaborators={toAccessRows(members)}
          max={4}
          size="md"
          withAccessLabels={false}
          onClick={() => surfaceApi.openPane('access')}
          testId="team-header-access-stack"
        />
      ) : null}
    >
      <div className="flex-1 flex flex-col min-h-0">
        <div data-tour-id="team-detail-tabs" className="flex-shrink-0 w-full">
          <DetailTabBar
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={goToTab}
            actions={(
              <PaneRail
                panels={panels}
                ariaLabel={t('Panels')}
                labels="auto"
                items={TEAM_SURFACE.railItems({
                  t,
                  icons: {
                    context: <PiInfoLight />,
                    access: <PiUsersLight />,
                    activity: <PiClockCounterClockwiseLight />,
                  },
                  // The record's own total, so the pill is right whether or
                  // not the pane has ever been opened.
                  badges: { access: members.length },
                })}
              />
            )}
          />
        </div>

        <DetailPageSurface
          surface={TEAM_SURFACE}
          layoutApi={panels}
          panes={[
            {
              id: 'context',
              title: t('Context'),
              icon: <PiInfoLight />,
              body: (
                <OrgContextPane
                  record={team}
                  onOpenAccess={() => surfaceApi.openPane('access')}
                  onOpenDocuments={() => goToTab('Documents')}
                />
              ),
            },
            {
              id: 'access',
              title: t('Members'),
              icon: <PiUsersLight />,
              body: (
                <AccessPane
                  collaborators={toAccessRows(members)}
                  onManageAccess={handleManageAccess}
                  canManage
                  ownerLabel="Lead"
                  emptyText={t('No members yet.')}
                />
              ),
            },
            {
              id: 'activity',
              title: t('Activity'),
              icon: <PiClockCounterClockwiseLight />,
              /* A thunk: the log fetches on mount, so it is built only while
                 the pane is actually on screen. No `hideHeader` — it reads
                 `useInPane()` and renders its body alone. */
              body: () => (
                <PaneBody>
                  <AuditLogPanel
                    entityType="Team"
                    entityId={teamId}
                    entityName={team.name}
                    onClose={() => panels.setVisible('activity', false)}
                  />
                </PaneBody>
              ),
            },
            {
              /* Opened by the Documents tab, never by the rail — see
                 `Shared/DocumentsTab`. */
              id: 'reader',
              title: readerContent?.title || t('Reader'),
              icon: <PiFileTextLight />,
              body: (
                <OrgReaderPane
                  content={readerContent}
                  onClose={() => {
                    panels.setVisible('reader', false);
                    setReaderContent(null);
                  }}
                />
              ),
            },
          ]}
        >
          {activeTab === 'Details' && (
            <div
              data-tour-id="team-detail-content"
              className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main"
            >
              <div className="mx-auto w-full max-w-6xl px-6 py-8 space-y-8">
                <GlassCard
                  icon={<PiTextAlignLeftLight />}
                  title={t('Description')}
                  padding="lg"
                  actions={!isEditingTeamInfo ? (
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => setIsEditingTeamInfo(true)}
                      icon={<PiPencilLight className="h-3.5 w-3.5" />}
                    >
                      {t('Edit')}
                    </Button>
                  ) : undefined}
                >
                  {isEditingTeamInfo ? (
                    <div className="space-y-4">
                      <Input
                        type="text"
                        label={t('Team Name')}
                        placeholder={t('Enter team name')}
                        value={editedTeamFields.name || ''}
                        onChange={(e) => handleTeamFieldChange('name', e.target.value)}
                        required
                        className="w-full"
                      />
                      <Textarea
                        label={t('Description')}
                        placeholder={t('Enter team description')}
                        value={editedTeamFields.description || ''}
                        onChange={(e) => handleTeamFieldChange('description', e.target.value)}
                        className="w-full"
                        id="team-description"
                      />
                      <Dropdown
                        label={t('Practice Areas')}
                        options={practiceAreaOptions}
                        value={(editedTeamFields.practice_area_ids || []).map(String)}
                        onChange={(vals) => handleTeamFieldChange('practice_area_ids', Array.isArray(vals) ? vals.map(Number) : [Number(vals)])}
                        multiple
                        fullWidth
                        placeholder={t('Select practice areas')}
                      />
                      <div className="flex justify-end gap-3 pt-3 border-t border-hairline-soft">
                        <Button variant="neutral" size="small" onClick={handleCancelEdit} disabled={isSaving}>
                          {t('Cancel')}
                        </Button>
                        {/* A plain wait, not an AI process: the neutral
                            Spinner, never the logo loader. */}
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleSaveTeam}
                          disabled={isSaving}
                          icon={isSaving ? <Spinner size="glyph" tone="current" /> : undefined}
                        >
                          {isSaving ? t('Saving...') : t('Save Changes')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    team.description ? (
                      <p className="text-sm text-fg-body leading-relaxed whitespace-pre-wrap max-w-prose">
                        {team.description}
                      </p>
                    ) : (
                      <p className="text-sm text-fg-muted">{t('No description provided')}</p>
                    )
                  )}
                </GlassCard>

                <FieldGrid columns={4}>
                  <FactTile icon={<PiCircleDashedLight />} label={t('Status')}>
                    <Badge color={team.is_active ? 'green' : 'gray'}>
                      {team.is_active ? t('Active') : t('Inactive')}
                    </Badge>
                  </FactTile>
                  <FactTile icon={<PiUsersLight />} label={t('Members')} value={members.length} />
                  <FactTile icon={<PiFileTextLight />} label={t('Documents')} value={team.document_count || 0} />
                  <FactTile
                    icon={<PiCalendarBlankLight />}
                    label={t('Created')}
                    value={team.created_at ? formatDate(team.created_at) : undefined}
                  />
                </FieldGrid>

                {team.practice_areas && team.practice_areas.length > 0 && (
                  <GlassCard icon={<PiTagLight />} title={t('Practice Areas')}>
                    <div className="flex flex-wrap gap-2">
                      {team.practice_areas.map((pa) => (
                        <Badge key={pa.id} color="blue">{pa.name}</Badge>
                      ))}
                    </div>
                  </GlassCard>
                )}
              </div>
            </div>
          )}

          {activeTab === 'Feed' && (
            <div className="flex-1 min-h-0 flex flex-col px-4 pb-3">
              <ActivityFeed
                events={feed}
                loading={feedLoading}
                onRefresh={loadFeed}
                onPostEvent={handlePostFeedEvent}
                onCommentAdd={handleAddComment}
                currentUser={userInfo ? {
                  id: userInfo.UserID || 'current-user',
                  first_name: userInfo.FirstName || 'You',
                  last_name: userInfo.LastName || '',
                  email: userInfo.Email || '',
                } : undefined}
                members={members}
              />
            </div>
          )}

          {activeTab === 'Documents' && (
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex-shrink-0 flex justify-end px-4 pt-3">
                <Button size="small" onClick={handleOpenDocumentUploadModal}>
                  <PiPlusCircleLight className="w-4 h-4 mr-1" />
                  {t('Add Document')}
                </Button>
              </div>
              <DocumentsTab
                domain="teams"
                resourceId={teamId}
                documents={documents}
                loading={documentsLoading}
                isUploading={isUploadingDocs}
                uploadingCount={uploadingDocCount}
                onUpload={handleUploadDocument}
                onDelete={handleDeleteDocument}
                onBulkDelete={handleBulkDeleteDocuments}
                onRefresh={loadDocuments}
              />
            </div>
          )}
        </DetailPageSurface>
      </div>

      <FeatureTour
        steps={teamDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </DetailFrame>
    </OrgSurfaceProvider>
  );
};

export default TeamDetail;
